import { createContext, useContext, useEffect, useState, useCallback } from "react";

const CartContext = createContext(null);
const KEY = "elnav_cart_v1";

const read = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
};
const write = (items) => localStorage.setItem(KEY, JSON.stringify(items));

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(read);

  useEffect(() => { write(items); }, [items]);

  // Sync across tabs
  useEffect(() => {
    const onStorage = (e) => { if (e.key === KEY) setItems(read()); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const add = useCallback((line) => {
    // line: { product_id, variant_id, name, image, size, color, price, quantity }
    setItems((prev) => {
      const idx = prev.findIndex(
        (i) => i.product_id === line.product_id && i.variant_id === line.variant_id
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + line.quantity };
        return next;
      }
      return [...prev, line];
    });
  }, []);

  const update = useCallback((product_id, variant_id, quantity) => {
    setItems((prev) =>
      prev
        .map((i) =>
          i.product_id === product_id && i.variant_id === variant_id
            ? { ...i, quantity: Math.max(1, quantity) }
            : i
        )
    );
  }, []);

  const remove = useCallback((product_id, variant_id) => {
    setItems((prev) =>
      prev.filter(
        (i) => !(i.product_id === product_id && i.variant_id === variant_id)
      )
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, add, update, remove, clear, count, subtotal }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
